import React, { useMemo, useState, useEffect } from 'react'
import { Bar } from 'react-chartjs-2'
import { t } from '@lingui/macro'
import { Box, Typography, Chip, Stack, FormControlLabel, Switch, Tabs, Tab, ToggleButtonGroup, ToggleButton } from '@mui/material'
import NoResults from '@/assets/svg/no-results.svg?react'
import Chart, { ChartData, TooltipItem } from '@/utils/chartjs-setup'
import { hexToRgb, getColorForType } from '../../lib/colorHelpers'

export type DurationDistributionProps = {
  data?: {
    durationDistribution?: {
      range: string
      count: number
      isOutlier: boolean
      type?: string
    }[]
    dataQuality?: {
      totalConsultations: number
      validDurations: number
      qualityScore: number
      zombieSessions: number
    }
  }
  title: string
  includeByType?: boolean
  selectedType?: string | null
}

// View mode types
type ViewMode = 'overall' | 'singleType' | 'compare'

export function DurationDistributionComponent({ data, title, includeByType = false, selectedType = null }: DurationDistributionProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('overall')
  const [typesToCompare, setTypesToCompare] = useState<string[]>([])

  // Extract all consultation types from data
  const types = useMemo(() => {
    if (!data?.durationDistribution?.length || !includeByType) return []

    const uniqueTypes = Array.from(
      new Set(
        data.durationDistribution
          .filter((d) => d.type)
          .map((d) => d.type)
          .filter((type): type is string => !!type)
      )
    )

    return uniqueTypes
  }, [data, includeByType])

  // When includeByType or selectedType changes, update view mode
  useEffect(() => {
    if (!includeByType) {
      setViewMode('overall')
    } else if (selectedType) {
      setViewMode('singleType')
    } else {
      setViewMode('overall')
    }
  }, [includeByType, selectedType])

  // Set initial types to compare when types are loaded
  useEffect(() => {
    if (types.length > 0 && typesToCompare.length === 0) {
      // Initially select first 3 types or all if less than 3
      setTypesToCompare(types.slice(0, Math.min(3, types.length)))
    }
  }, [types, typesToCompare.length])

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode) {
      setViewMode(newMode)
    }
  }

  const handleTypesToCompareChange = (event: React.MouseEvent<HTMLElement>, newTypes: string[]) => {
    setTypesToCompare(newTypes.length ? newTypes : [])
  }

  const getDistributionForType = (type: string) => {
    return data?.durationDistribution?.filter((d) => d.type === type) || []
  }

  // Get a properly formatted display name for consultation types
  function getTypeDisplayName(type: string): string {
    const typeDisplayNames: Record<string, string> = {
      classic: t`Classic`,
      emergency: t`Emergency`,
      local: t`Local`,
      meeting_only: t`Meeting Only`,
      netsoins: t`Netsoins`,
      pedcl: t`PEDCL`,
    }

    return typeDisplayNames[type] || type
  }


  const { chartData, hasData, isStackedView } = useMemo(() => {
    if (!data?.durationDistribution?.length) {
      return { chartData: null, hasData: false, isStackedView: false }
    }

    let chartData: ChartData<'bar'>
    let isStacked = false

    // Sort ranges in correct order (custom sort function needed for ranges like '>180')
    const sortedDistribution = [...data.durationDistribution].sort((a, b) => {
      if (a.range.startsWith('>') && !b.range.startsWith('>')) return 1
      if (!a.range.startsWith('>') && b.range.startsWith('>')) return -1

      const [aMin] = a.range.startsWith('>') ? [parseInt(a.range.substring(1))] : a.range.split('-').map(Number)

      const [bMin] = b.range.startsWith('>') ? [parseInt(b.range.substring(1))] : b.range.split('-').map(Number)

      return aMin - bMin
    })

    // Get unique ranges
    const uniqueRanges = Array.from(new Set(sortedDistribution.map((item) => item.range))).sort((a, b) => {
      if (a.startsWith('>') && !b.startsWith('>')) return 1
      if (!a.startsWith('>') && b.startsWith('>')) return -1

      const [aMin] = a.startsWith('>') ? [parseInt(a.substring(1))] : a.split('-').map(Number)

      const [bMin] = b.startsWith('>') ? [parseInt(b.substring(1))] : b.split('-').map(Number)

      return aMin - bMin
    })

    if (viewMode === 'compare' && includeByType && typesToCompare.length > 0) {
      chartData = {
        labels: uniqueRanges,
        datasets: typesToCompare.map((type) => {
          const typeData = getDistributionForType(type)
          return {
            label: getTypeDisplayName(type),
            data: uniqueRanges.map((range) => {
              const rangeData = typeData.find((d) => d.range === range)
              return rangeData ? rangeData.count : 0
            }),
            backgroundColor: `rgba(${hexToRgb(getColorForType(type))}, 0.7)`,
            borderColor: `rgba(${hexToRgb(getColorForType(type))}, 1)`,
            borderWidth: 1,
            barPercentage: 0.8,
            categoryPercentage: 0.9 / typesToCompare.length,
          }
        }),
      }
    } else if (viewMode === 'singleType' && includeByType && selectedType) {
      const typeData = getDistributionForType(selectedType)

      if (!typeData.length) {
        return { chartData: null, hasData: false, isStackedView: false }
      }

      chartData = {
        labels: uniqueRanges,
        datasets: [
          {
            label: getTypeDisplayName(selectedType),
            data: uniqueRanges.map((range) => {
              const rangeData = typeData.find((d) => d.range === range)
              return rangeData ? rangeData.count : 0
            }),
            backgroundColor: uniqueRanges.map((range) => {
              const rangeData = typeData.find((d) => d.range === range)
              return rangeData?.isOutlier ? `rgba(255, 99, 132, 0.5)` : `rgba(${hexToRgb(getColorForType(selectedType))}, 0.5)`
            }),
            borderColor: uniqueRanges.map((range) => {
              const rangeData = typeData.find((d) => d.range === range)
              return rangeData?.isOutlier ? `rgba(255, 99, 132, 1)` : `rgba(${hexToRgb(getColorForType(selectedType))}, 1)`
            }),
            borderWidth: 1,
          },
        ],
      }
    } else {
      const filteredDistribution = selectedType ? sortedDistribution.filter((d) => d.type === selectedType) : sortedDistribution

      chartData = {
        labels: uniqueRanges,
        datasets: [
          {
            label: selectedType ? `${getTypeDisplayName(selectedType)} - ${t`Consultation count`}` : t`Consultation count`,
            data: uniqueRanges.map((range) => {
              return filteredDistribution.filter((d) => d.range === range).reduce((sum, current) => sum + current.count, 0)
            }),
            backgroundColor: uniqueRanges.map((range) => {
              const rangeData = filteredDistribution.find((d) => d.range === range)
              if (selectedType) {
                return rangeData?.isOutlier ? `rgba(255, 99, 132, 0.5)` : `rgba(${hexToRgb(getColorForType(selectedType))}, 0.5)`
              }
              return rangeData?.isOutlier ? 'rgba(255, 99, 132, 0.5)' : 'rgba(54, 162, 235, 0.5)'
            }),
            borderColor: uniqueRanges.map((range) => {
              const rangeData = filteredDistribution.find((d) => d.range === range)
              if (selectedType) {
                return rangeData?.isOutlier ? `rgba(255, 99, 132, 1)` : `rgba(${hexToRgb(getColorForType(selectedType))}, 1)`
              }
              return rangeData?.isOutlier ? 'rgba(255, 99, 132, 1)' : 'rgba(54, 162, 235, 1)'
            }),
            borderWidth: 1,
          },
        ],
      }
    }

    return { chartData, hasData: true, isStackedView: isStacked }
  }, [data, viewMode, includeByType, types, selectedType, typesToCompare])


  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            boxWidth: 15,
            padding: 10,
          },
        },
        tooltip: {
          callbacks: {
            label: (context: TooltipItem<'bar'>) => {
              const value = context.formattedValue
              let label = context.dataset.label || ''
              if (label) label += ': '

              label += `${value} ${t`Consultations`}`

              if (!isStackedView && viewMode !== 'compare') {
                const isOutlier = data?.durationDistribution?.find(
                  (d) => d.range === context.label && (selectedType ? d.type === selectedType : true)
                )?.isOutlier

                if (isOutlier) {
                  label += ` (${t`Outliers included`})`
                }
              }

              return label
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: t`Minute ranges`,
          },
          stacked: isStackedView,
        },
        y: {
          beginAtZero: true,
          stacked: isStackedView,
          title: {
            display: true,
            text: t`Consultation count`,
          },
        },
      },
    }
  }, [isStackedView, data, selectedType, viewMode])

  const normalizedChartData = useMemo(() => {
    if (viewMode !== 'compare' || !chartData || !includeByType || typesToCompare.length === 0) {
      return null
    }

    const normalizedData = JSON.parse(JSON.stringify(chartData)) as ChartData<'bar'>

    const typeTotals = normalizedData.datasets.map((dataset) => {
      const numericData = dataset.data.map((val) => {
        return typeof val === 'number' ? val : 0
      })
      return numericData.reduce((sum, val) => sum + val, 0)
    })

    normalizedData.datasets.forEach((dataset, i) => {
      const total = typeTotals[i]
      if (total && total > 0) {
        dataset.data = dataset.data.map((val) => {
          const numericVal = typeof val === 'number' ? val : 0
          return total > 0 ? Math.round((numericVal / total) * 100) : 0
        })
      }
      dataset.label = `${dataset.label} (%)`
    })

    return normalizedData
  }, [chartData, viewMode, includeByType, typesToCompare])

  const normalizedOptions = useMemo(() => {
    if (!options) return null

    const newOptions = JSON.parse(JSON.stringify(options))
    newOptions.scales.y.title.text = t`Percentage of consultations`
    newOptions.plugins.tooltip.callbacks.label = (context: TooltipItem<'bar'>) => {
      const value = context.formattedValue
      let label = context.dataset.label || ''
      if (label) label += ': '
      return `${label}${value}%`
    }

    return newOptions
  }, [options])

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h2">{title}</Typography>
        {data?.dataQuality && (
          <Chip
            label={`${t`Data Quality`}: ${data.dataQuality.qualityScore}%`}
            color={data.dataQuality.qualityScore > 75 ? 'success' : data.dataQuality.qualityScore > 50 ? 'warning' : 'error'}
          />
        )}
      </Stack>

      {includeByType && (
        <Box sx={{ mb: 3 }}>
          {!selectedType && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{
                mb: 2,
                flexWrap: { xs: 'wrap', md: 'nowrap' },
              }}
            >
              <Typography variant="subtitle1">{t`View mode`}:</Typography>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                aria-label="view mode"
                size="small"
                sx={{
                  flexGrow: 1,
                  justifyContent: 'center',
                  '& .MuiToggleButton-root': {
                    px: 2,
                    py: 1,
                  },
                }}
              >
                <ToggleButton value="overall">{t`Overall`}</ToggleButton>
                <ToggleButton value="compare">{t`Compare`}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}

          {viewMode === 'compare' && types.length > 0 && !selectedType && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t`Select types to compare`}:
              </Typography>
              <Box
                sx={{
                  overflowX: 'auto',
                  pb: 1,
                }}
              >
                <ToggleButtonGroup
                  value={typesToCompare}
                  onChange={handleTypesToCompareChange}
                  aria-label="types to compare"
                  size="small"
                  sx={{
                    flexWrap: { xs: 'nowrap', md: 'wrap' },
                    '& .MuiToggleButton-root': {
                      px: 2,
                      py: 0.5,
                      minWidth: 'fit-content',
                    },
                  }}
                >
                  {types.map((type) => (
                    <ToggleButton
                      key={type}
                      value={type}
                      sx={{
                        '&.Mui-selected': {
                          color: getColorForType(type),
                          fontWeight: 'bold',
                        },
                      }}
                    >
                      {getTypeDisplayName(type)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {hasData && chartData ? (
        <Box>
          <Box sx={{ height: viewMode === 'compare' && normalizedChartData ? 300 : 400, mt: 3 }}>
            <Bar data={chartData} options={options} />
          </Box>

          {viewMode === 'compare' && normalizedChartData && normalizedOptions && (
            <Box
              sx={{
                height: 300,
                mt: 4,
                pt: 2,
                borderTop: '1px dashed #ccc',
              }}
            >
              <Typography variant="subtitle1" gutterBottom>
                {t`Normalized comparison`}
              </Typography>
              <Bar data={normalizedChartData} options={normalizedOptions} />
            </Box>
          )}

          {data?.dataQuality?.zombieSessions && data.dataQuality.zombieSessions > 0 && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              {t`Zombie sessions`}: {data.dataQuality.zombieSessions}
            </Typography>
          )}
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="300px">
          <NoResults style={{ maxWidth: '40%', height: 'auto' }} />
          <Typography variant="subtitle1" color="text.secondary" mt={2}>
            {t`No data`}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default DurationDistributionComponent
